/**
 * Emissions Scope Sub-Navigation
 *
 * Page-level sub-tabs for Scope 1/2/3 within Emissions Insights section.
 */

import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl } from '@routes/util';
import { SCOPE_LUCIDE_ICONS, SCOPE_COLORS, SCOPE_TAILWIND } from '../config/scopeBranding';

interface ScopeTab {
  key: string;
  label: string;
  route: keyof typeof emissionsTrackerRoutes;
}

const scopeTabs: ScopeTab[] = [
  { key: 'scope1', label: 'Scope 1', route: 'EMISSIONS_SCOPE1' },
  { key: 'scope2', label: 'Scope 2', route: 'EMISSIONS_SCOPE2' },
  { key: 'scope3', label: 'Scope 3', route: 'EMISSIONS_SCOPE3' },
];

export const EmissionsScopeNav = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const history = useHistory();

  const scope1Match = useRouteMatch(emissionsTrackerRoutes.EMISSIONS_SCOPE1.path);
  const scope2Match = useRouteMatch(emissionsTrackerRoutes.EMISSIONS_SCOPE2.path);
  const scope3Match = useRouteMatch(emissionsTrackerRoutes.EMISSIONS_SCOPE3.path);

  const getActiveScope = (): string => {
    if (scope1Match) return 'scope1';
    if (scope2Match) return 'scope2';
    if (scope3Match) return 'scope3';
    return 'scope1';
  };

  const activeScope = getActiveScope();

  const getUrl = (routeKey: keyof typeof emissionsTrackerRoutes): string => {
    const route = emissionsTrackerRoutes[routeKey];
    if (!route) return '#';
    return generateUrl(route, { rootAppPath, initiativeId });
  };

  return (
    <div className='tw:flex tw:w-full tw:gap-2 tw:mb-4'>
      {scopeTabs.map((tab) => {
        const isActive = activeScope === tab.key;
        const Icon = SCOPE_LUCIDE_ICONS[tab.key];
        const color = SCOPE_COLORS[tab.key];
        const tw = SCOPE_TAILWIND[tab.key];

        return (
          <button
            key={tab.key}
            type='button'
            onClick={() => history.push(getUrl(tab.route))}
            className={cn(
              'tw:flex-1 tw:flex tw:items-center tw:justify-center tw:gap-2',
              'tw:rounded-md tw:border tw:px-4 tw:py-2 tw:text-sm tw:font-medium',
              'tw:transition-colors tw:cursor-pointer',
              isActive
                ? 'tw:text-white tw:border-transparent'
                : cn('tw:bg-white tw:border-current tw:hover:opacity-80', tw.icon)
            )}
            style={isActive ? { backgroundColor: color, borderColor: color } : { color }}
          >
            {Icon && <Icon className='tw:size-4' />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
